/**
 * Funções utilitárias para gerenciamento de storage através de múltiplos canais
 * (localStorage, sessionStorage, cookies)
 */

/**
 * Salva um valor em todos os tipos de armazenamento disponíveis
 * @param {string} key - A chave para o armazenamento
 * @param {any} value - O valor a ser armazenado (será convertido para string)
 * @param {Object} options - Opções adicionais (como expiração de cookie)
 */
export const saveInStorage = (key, value, options = {}) => {
  if (!key) return;

  const stringValue = typeof value === 'string' ? value : JSON.stringify(value);

  // Salvar em todos os tipos de storage disponíveis
  try {
    localStorage.setItem(key, stringValue);
    sessionStorage.setItem(key, stringValue);

    // Cookie com opções extras
    let cookieStr = `${key}=${encodeURIComponent(stringValue)}; path=/`;

    if (options.expireDays) {
      const date = new Date();
      date.setTime(date.getTime() + options.expireDays * 24 * 60 * 60 * 1000);
      cookieStr += `; expires=${date.toUTCString()}`;
    }

    if (options.sameSite) {
      cookieStr += `; SameSite=${options.sameSite}`;
    }

    if (options.secure) {
      cookieStr += '; Secure';
    }

    document.cookie = cookieStr;
  } catch (e) {
    console.warn(`Falha ao salvar em storage: ${e.message}`);
  }
};

/**
 * Recupera um valor de qualquer tipo de armazenamento disponível
 * @param {string} key - A chave para buscar
 * @param {any} defaultValue - Valor padrão caso não encontre
 * @returns {any} O valor encontrado ou o valor padrão
 */
export const getFromStorage = (key, defaultValue = null) => {
  if (!key) return defaultValue;

  try {
    // Tentar localStorge primeiro
    const localValue = localStorage.getItem(key);
    if (localValue !== null) {
      return tryParse(localValue);
    }

    // Depois sessionStorage
    const sessionValue = sessionStorage.getItem(key);
    if (sessionValue !== null) {
      return tryParse(sessionValue);
    }

    // Por último, cookies
    const cookieValue = document.cookie
      .split('; ')
      .find((row) => row.startsWith(`${key}=`))
      ?.split('=')[1];

    if (cookieValue) {
      return tryParse(decodeURIComponent(cookieValue));
    }
  } catch (e) {
    console.warn(`Erro ao recuperar de storage: ${e.message}`);
  }

  return defaultValue;
};

/**
 * Remove um valor de todos os tipos de armazenamento
 * @param {string} key - A chave a ser removida
 */
export const removeFromStorage = (key) => {
  if (!key) return;

  try {
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
    document.cookie = `${key}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
  } catch (e) {
    console.warn(`Erro ao remover de storage: ${e.message}`);
  }
};

/**
 * Tenta fazer parse de um valor JSON, retornando o original se falhar
 * @param {string} value - O valor para tentar converter
 * @returns {any} O valor convertido ou o original
 */
const tryParse = (value) => {
  if (typeof value !== 'string') return value;

  try {
    const parsed = JSON.parse(value);
    return parsed;
  } catch (e) {
    return value;
  }
};

export default {
  saveInStorage,
  getFromStorage,
  removeFromStorage,
};
